import { useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

import type { DashboardCustomer } from '@models/customers';
import {
  getScheduledPaymentAmountLabel,
  getScheduledPaymentChargerLabel,
  getScheduledPaymentStatusLabel,
  getScheduledPaymentStatusTone,
  getScheduledPaymentTimeLeft,
  getSubscriptionLabel,
  isNutramegTestCustomer,
} from '@features/customers/helpers';
import { formatPaymentDate } from '@features/payments/helpers';

import StatusChip from '@components/ui/StatusChip';
import ExecutePaymentButton from '../ExecutePaymentButton';
import s from './CustomerDetailPanel.module.css';

// Backend caps the hydrated list to the most-recent 25 (dashboard-customers.queries.ts).
const SCHEDULED_PAYMENTS_CAP = 25;

type CustomerScheduledPaymentsTabProps = {
  customer: DashboardCustomer;
};

function formatDateTimeLabel(value: string | null): string {
  if (!value) return 'N/A';
  const fd = formatPaymentDate(new Date(value));
  return `${fd.date} · ${fd.time}`;
}

// Shows the first 4 chars of an id; click copies the full value to the clipboard.
function CopyableId({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard
      .writeText(id)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  };

  return (
    <button type="button" className={s.idCopy} onClick={copy} title={`Copy ${id}`}>
      {copied ? (
        <>
          <CheckIcon sx={{ fontSize: 13 }} /> Copied
        </>
      ) : (
        <>
          {id.slice(0, 4)}… <ContentCopyIcon sx={{ fontSize: 12 }} />
        </>
      )}
    </button>
  );
}

function CustomerScheduledPaymentsTab({ customer }: CustomerScheduledPaymentsTabProps) {
  const scheduledPayments = customer.scheduledPayments ?? [];

  if (scheduledPayments.length === 0) {
    return (
      <div className={s.tableCard}>
        <div className={s.tabEmpty}>No scheduled payments.</div>
      </div>
    );
  }

  // Debt/recovery rows show "charge of remaining left"; look up their balances by id (both arrays are
  // fully hydrated on the customer, not capped).
  const debtById = new Map((customer.debtCollections ?? []).map((debt) => [debt.id, debt]));
  const recoveryById = new Map(
    (customer.subscriptionRecoveries ?? []).map((recovery) => [recovery.id, recovery]),
  );

  const showExecuteAction = isNutramegTestCustomer(customer);

  return (
    <div className={s.tableCard}>
      <table className={s.miniTable}>
        <thead>
          <tr>
            <th>ID</th>
            <th>Provider ID</th>
            <th>Execute at</th>
            <th>Time left</th>
            <th>Status</th>
            <th>Amount</th>
            <th>Descriptor</th>
            <th>Subscription</th>
            <th>Charger</th>
            <th>Retry</th>
            <th>Error</th>
            {showExecuteAction && <th aria-label="Actions" />}
            <th>Created</th>
            <th>Updated</th>
          </tr>
        </thead>
        <tbody>
          {scheduledPayments.map((sp) => {
            const error = sp.previousErrorCode || sp.systemError || '';
            const debt = sp.debtCollectionId ? debtById.get(sp.debtCollectionId) : undefined;
            const recovery = sp.subscriptionRecoveryId
              ? recoveryById.get(sp.subscriptionRecoveryId)
              : undefined;
            const amountLabel = getScheduledPaymentAmountLabel(sp, debt, recovery);
            return (
              <tr key={sp.id}>
                <td>
                  <CopyableId id={sp.id} />
                </td>
                <td className={s.mono}>{sp.providerId || '—'}</td>
                <td>{formatDateTimeLabel(sp.executeAt)}</td>
                <td>{getScheduledPaymentTimeLeft(sp.status, sp.executeAt)}</td>
                <td>
                  <StatusChip
                    label={getScheduledPaymentStatusLabel(sp.status)}
                    tone={getScheduledPaymentStatusTone(sp.status)}
                  />
                </td>
                <td className={s.amountCell}>{amountLabel}</td>
                <td>{sp.paymentDescriptor || '—'}</td>
                <td>{getSubscriptionLabel(sp) || '—'}</td>
                <td>{getScheduledPaymentChargerLabel(sp)}</td>
                <td>{sp.retryTime}</td>
                <td className={s.mono} title={error || undefined}>
                  {error || '—'}
                </td>
                {showExecuteAction && (
                  <td>
                    {sp.status === 'upcoming' && (
                      <ExecutePaymentButton customerId={customer.id} scheduledPaymentId={sp.id} />
                    )}
                  </td>
                )}
                <td>{formatDateTimeLabel(sp.createdAt)}</td>
                <td>{formatDateTimeLabel(sp.updatedAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {scheduledPayments.length >= SCHEDULED_PAYMENTS_CAP && (
        <div className={s.tabEmpty}>
          Showing the {SCHEDULED_PAYMENTS_CAP} most recent scheduled payments.
        </div>
      )}
    </div>
  );
}

export default CustomerScheduledPaymentsTab;
